<script lang="ts">
  import type { Game, Maybe } from '$lib/types';
  import type { Api_Game, Api_Image } from '$lib/types/api/graphql';

  import { useActions, type ActionArray } from '$components/actions';
  import Image from '$mds/Image';
  import type { IconComponentType } from '../IconWrapper';
  import IconWrapper from '../IconWrapper';
  import { createEventDispatcher } from 'svelte';
  const dispatch = createEventDispatcher();

  export { classNames as class };
  let classNames = '';
  export let game: Maybe<Api_Game> | Maybe<Game> | undefined = null;
  export let imagePath: string | undefined = undefined;
  export let icon: IconComponentType | null = null;
  export let iconClass: string = '';
  export let iconButtonClass: string = '';
  export let label: string | null | undefined = '';
  export let labelClass: string = '';
  export let labelValue: string | number = '';
  export let labelWrapperClass: string = '';
  export let variant: 'default' | 'selected' | 'disabled' | 'danger' = 'default';
  export let size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'sm';
  export let title: string | undefined = undefined;
  export let href: string | undefined = undefined;
  export let disabled = false;
  export let iconPosition: 'left' | 'right' = 'left';
  export let target = '';
  export let use: ActionArray = [];

  $: elTag = href ? 'a' : 'button';

  $: leftImage = (game?.logoMain as Api_Image)?.original?.url || (game?.logoMain as string) || imagePath;

  $: padding = {
    xs: `py-1 ${leftImage ? 'pl-1 pr-2' : 'px-2.5'}`,
    sm: `py-1.5 ${leftImage ? 'pl-1.5 pr-3' : 'px-4'}`,
    md: `py-1.5 ${leftImage ? 'pl-1.5 pr-3' : 'px-4.5'}`,
    lg: `py-1.5 ${leftImage ? 'pl-1.5 pr-3' : 'px-5'}`,
    xl: `py-1.5 ${leftImage ? 'pl-1.5 pr-3' : 'px-6'}`
  };

  const variantClasses = {
    default: 'text-ondark-secondary border border-ondark-subtle bg-ondark-low cursor-pointer',
    selected: 'text-ondark-primary border border-ondark-primary bg-ondark-high cursor-pointer',
    disabled: 'text-ondark-subtle border border-transparent bg-ondark-low cursor-default pointer-events-none',
    danger: 'text-alert-ondark-base ring-2 ring-alert-ondark-base bg-alert-ondark-subtle cursor-default'
  };

  const sizes = {
    xs: 'h-6 w-fit gap-1',
    sm: 'h-8 w-fit gap-2',
    md: 'h-9 w-fit  gap-2',
    lg: 'h-11 w-fit gap-2',
    xl: 'h-15 w-fit gap-2'
  };

  const imageSizes = {
    xs: 'size-4',
    sm: 'size-5',
    md: 'size-6',
    lg: 'size-8',
    xl: 'size-11'
  };

  const imageSizeset = {
    xs: 16,
    sm: 20,
    md: 24,
    lg: 32,
    xl: 44
  };

  const textSizes = {
    xs: 'text-body-3xs',
    sm: 'text-body-3xs',
    md: 'text-body-xs',
    lg: 'text-body-md',
    xl: 'text-body-lg'
  };

  const iconSizes = {
    xs: 'base',
    sm: 'base',
    md: 'base',
    lg: 'md',
    xl: 'lg'
  };
</script>

<svelte:element
  this={elTag}
  role="button"
  {disabled}
  tabindex="0"
  class="hover:bg-ondark-medium focus:ring-ondark-focused flex w-max items-center justify-center whitespace-nowrap rounded-full outline-none transition-all focus:ring-2 {variantClasses[
    variant
  ]} {disabled ? 'pointer-events-none' : ''} {classNames} {sizes[size]} {padding[size]}"
  {title}
  {href}
  {target}
  on:click
  on:dblclick
  use:useActions={use}
>
  <slot name="icon">
    {#if leftImage}
      <Image
        src={leftImage}
        class="object-cover {imageSizes[size]}"
        sizes={{ xs: imageSizeset[size] }}
        rounded="rounded-full"
        fit="cover"
        alt={game?.localizedTitles?.en || game?.title?.en || game?.title}
      />
    {:else if icon && iconPosition === 'left'}
      <IconWrapper {icon} size={iconSizes[size]} class={iconClass} />
    {/if}
  </slot>

  <div class="{textSizes[size]} {labelWrapperClass} flex gap-2">
    {#if label}
      <span class={labelClass}>{label}</span>
    {:else}
      <slot />
    {/if}

    {#if labelValue}
      <span class="text-ondark-subtle font-normal">{labelValue}</span>
    {/if}
  </div>

  {#if icon && iconPosition === 'right'}
    <button class="{iconButtonClass} flex" type="button" on:click={() => dispatch('clickIcon')}>
      <IconWrapper {icon} size={iconSizes[size]} class={iconClass} />
    </button>
  {/if}
</svelte:element>
